import { z } from 'zod'

import { residentialQuoteSchema, type ResidentialFormData } from '@/lib/schemas/quote'

/**
 * The /rock-walls inline form. Same rules as the quote page for the fields
 * it shares, plus an optional wall length and height that ride in the
 * description so nothing downstream (table, admin, email) has to learn a new
 * column. Length was required from 20 Oct and the page's leads stopped, so it
 * went back to optional on 25 Oct: a guess is welcome, a blank is fine.
 */
export const wallEnquirySchema = residentialQuoteSchema
  .pick({
    name: true,
    phone: true,
    postcode: true,
    jobAddress: true,
    placeId: true,
    lat: true,
    lng: true,
  })
  .extend({
    wallLength: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().max(200, 'Keep it short.'))
      .optional()
      .default(''),
    wallHeight: z
      .string()
      .transform((v) => v.trim())
      .pipe(z.string().max(200, 'Keep the height short.'))
      .optional()
      .default(''),
  })

export type WallEnquiryFormData = z.input<typeof wallEnquirySchema>

export function buildWallEnquiryBody(
  data: WallEnquiryFormData
): ResidentialFormData & { source: 'rock-walls-form' } {
  const length = (data.wallLength ?? '').trim()
  const height = (data.wallHeight ?? '').trim()
  const lines = [
    length && `Approx wall length: ${length}`,
    height && `Approx wall height: ${height}`,
  ].filter(Boolean)
  return {
    source: 'rock-walls-form',
    enquiryType: 'residential',
    name: data.name,
    phone: data.phone,
    postcode: data.postcode,
    ...(data.jobAddress ? { jobAddress: data.jobAddress } : {}),
    ...(data.placeId ? { placeId: data.placeId, lat: data.lat, lng: data.lng } : {}),
    email: '',
    service: 'rock-walls',
    description: lines.join('\n'),
  }
}
